import { activeSiteConfig } from "@config/site/site-config";
import { CommonCMS } from "@lib/constants/contentful";
import { useMicrocopy } from "@lib/contentful/microcopy/MicrocopyContext";
import { Text } from "@ui/components/content/text/Text";
import { Money } from "@ui/components/data-display/money/Money";
import type { SimpleMoneyData } from "@ui/components/data-display/money/useMoney";
import { IconButton } from "@ui/components/forms/icon-button/IconButton";
import { Box } from "@ui/components/layout/box/Box";
import type { FlexProps } from "@ui/components/layout/flex/Flex";
import { Flex } from "@ui/components/layout/flex/Flex";
import { ECCOIcon } from "@ui/components/media-and-icons/ecco-icon/ECCOIcon";
import { useDisclosure } from "@ui/hooks/useDisclosure";
import { useBreakpoint } from "@ui/hooks/useBreakpoint";
import { productNameTransform } from "@ui/utils/productNameTransform";
import type { FC, MouseEventHandler } from "react";
import React, { useCallback, useMemo, useState } from "react";
import { useInView } from "react-intersection-observer";
import { ProductImages } from "./product-images/ProductImages";
import { LinkStyles, ProductTileTheme } from "./ProductTile.styles";
import { getPDPSlug } from "@lib/utils/getPDPSlug";
import { NextLink } from "@components/ui/next-link/NextLink";
import { calculateDisplayPrices } from "@components/commerce/helpers";
import type { SaleBadge } from "@lib/wrappers/algolia/types";

export interface ProductTileProps extends FlexProps {
    theme?: string;
    productName: string;
    articleNumber: string;
    colorCode: string;
    image: string;
    imageLabel?: string;
    variants?: Variant[];
    productColors: {
        colorCode: string;
        image: string;
        imageLabel?: string;
        priceWithoutDiscount: Price;
        objectID: string;
        altTagPattern?: string;
        priceWithDiscount: Price;
        hasDiscount: boolean;
        eaBadge?: SaleBadge;
        hasStock?: boolean;
        discountPercentage: number;
        lowestPrice?: Price;
        discountPercentageLowestPrice?: number;
    }[];
    objectID: string;
    categories?: string[];
    turnOffHover?: boolean;
    aspectRatioImage?: string | string[];
    altTagPattern?: string;
    handleTileClick?: MouseEventHandler<HTMLAnchorElement>;
    hasDiscount?: boolean;
    eaBadge?: SaleBadge;
    hasStock?: boolean;
    locale: string;
    priceWithoutDiscount: Price;
    priceWithDiscount: Price;
    discountPercentage: number;
    colorLabel: string;
    tagComponent?: React.ReactElement;
    isAddingToWishlist?: boolean;
    onAddToWishlist?: (product: Product) => Promise<void>;
    descriptiveHeading?: string;
    onInViewPort?: (inView: boolean, item: any) => any;
    customBadges?: string[];
    lowestPrice?: Price;
    discountPercentageLowestPrice?: number;
}

type Price = { centAmount: number; currencyCode: string; fractionDigits: number };
export type Variant = { size: string; availableQuantity: number; sku: string };

export type Product = Pick<
    ProductTileProps,
    | "priceWithDiscount"
    | "priceWithoutDiscount"
    | "image"
    | "imageLabel"
    | "variants"
    | "objectID"
    | "altTagPattern"
    | "colorCode"
    | "discountPercentage"
    | "hasDiscount"
    | "eaBadge"
    | "hasStock"
    | "lowestPrice"
    | "discountPercentageLowestPrice"
>;

export const ProductTile: FC<ProductTileProps> = ({
    productName,
    articleNumber,
    colorCode,
    categories,
    image,
    imageLabel,
    variants,
    productColors,
    objectID,
    turnOffHover = false,
    aspectRatioImage,
    altTagPattern,
    handleTileClick,
    hasDiscount = false,
    eaBadge,
    hasStock,
    locale,
    priceWithoutDiscount,
    priceWithDiscount,
    discountPercentage,
    colorLabel,
    isAddingToWishlist,
    onAddToWishlist,
    descriptiveHeading,
    onInViewPort,
    customBadges,
    lowestPrice,
    discountPercentageLowestPrice,
    theme,
    ...rest
}) => {
    const { isOpen: isExpanded, onOpen, onClose } = useDisclosure();
    const md = useBreakpoint("md");
    const isMobile = md === false;

    const { getMultiple } = useMicrocopy();
    const microcopies = getMultiple(CommonCMS.shared, [
        CommonCMS.price.recommendedPrice,
        CommonCMS.price.discountedPrice,
        CommonCMS.price.previousLowestPrice,
    ]);

    const initialProduct = useMemo<Product>(() => {
        return {
            priceWithDiscount,
            priceWithoutDiscount,
            image,
            imageLabel,
            variants,
            objectID,
            altTagPattern,
            colorCode,
            discountPercentage,
            hasDiscount,
            eaBadge,
            hasStock,
            customBadges,
            lowestPrice,
            discountPercentageLowestPrice,
        };
    }, [
        colorCode,
        priceWithoutDiscount,
        objectID,
        priceWithDiscount,
        image,
        imageLabel,
        altTagPattern,
        variants,
        hasDiscount,
        eaBadge,
        hasStock,
        discountPercentage,
        customBadges,
        lowestPrice,
        discountPercentageLowestPrice,
    ]);

    const [displayedProduct, setDisplayedProduct] = useState<Product>(initialProduct);

    const handleOnOpen = () => {
        !turnOffHover && onOpen();
    };

    const handleOnClose = () => {
        !turnOffHover && onClose();
    };

    const { slug: productSlug } = useMemo(
        () => getPDPSlug(productName, articleNumber, displayedProduct.colorCode, locale),
        [productName, articleNumber, displayedProduct.colorCode, locale]
    );

    const addToWishlist = useCallback(async () => {
        if (onAddToWishlist) {
            await onAddToWishlist(initialProduct);
        }
    }, [initialProduct]);

    const { ref } = useInView({
        threshold: 0.5,
        onChange: (inView) => {
            if (onInViewPort) {
                onInViewPort(inView, objectID);
            }
        },
    });

    // We need to check if the next item is a button, if not we close the modal (that means is a link thus is another product tile)
    const handleOnBlur = (e: React.FocusEvent<HTMLDivElement>) => {
        if (e.relatedTarget?.classList.contains("chakra-link")) {
            onClose();
        }
    };

    const isOmnibusMarket = activeSiteConfig?.isOmnibusMarket;
    const isDiscounted = displayedProduct.discountPercentage > 0;
    const currentPrice = displayedProduct.priceWithDiscount;

    const { displayPreviousPrice, displayDiscountPercentage } = useMemo(
        () =>
            calculateDisplayPrices(
                isOmnibusMarket,
                currentPrice?.centAmount,
                displayedProduct.discountPercentage,
                displayedProduct.lowestPrice,
                displayedProduct.discountPercentageLowestPrice,
                displayedProduct.priceWithoutDiscount
            ),
        [
            isOmnibusMarket,
            currentPrice,
            displayedProduct.discountPercentage,
            displayedProduct.lowestPrice,
            displayedProduct.discountPercentageLowestPrice,
            displayedProduct.priceWithoutDiscount,
        ]
    );

    const hasPreviousLowestPriceFinal = !!displayPreviousPrice?.centAmount;
    const showPreviousLowestPrice =
        isDiscounted && hasPreviousLowestPriceFinal && displayDiscountPercentage > 0;

    const showRecommendedPrice =
        (!isOmnibusMarket || showPreviousLowestPrice) && displayedProduct?.discountPercentage > 0;

    const themeSurface = useMemo(() => ProductTileTheme[theme]?.surface, [theme]);
    const themeText = useMemo(() => ProductTileTheme[theme]?.text, [theme]);
    const themeTextGray = useMemo(() => ProductTileTheme[theme]?.textGray, [theme]);

    return (
        <Box
            data-testid="ProductTile"
            h={["full"]}
            ref={ref}
            {...rest}
            onFocus={onOpen}
            position="relative"
            onBlur={handleOnBlur}
            {...themeSurface}
        >
            <NextLink
                href={productSlug}
                onClick={handleTileClick}
                sx={LinkStyles}
                prefetch={false}
                tabIndex={0}
            >
                <Box
                    display={["flex", "flex"]}
                    flexDirection={"column"}
                    gridTemplateRows={turnOffHover ? "70% 30%" : ["70% 0% 30%", "70% 15% 15%"]}
                    h={["auto", "auto"]}
                    backgroundColor={"white"}
                    aria-label={productName}
                    {...themeSurface}
                >
                    <ProductImages
                        setDisplayedProduct={setDisplayedProduct}
                        displayedProduct={displayedProduct}
                        initialProduct={initialProduct}
                        altTagPattern={altTagPattern}
                        productColors={productColors}
                        productName={productName}
                        isExpanded={isExpanded}
                        onOpen={handleOnOpen}
                        onClose={handleOnClose}
                        aspectRatioImage={aspectRatioImage}
                        productSlug={productSlug}
                        discountPercentage={
                            isDiscounted &&
                            displayDiscountPercentage > 0 &&
                            displayDiscountPercentage
                        }
                        customBadges={customBadges}
                    />
                    <Box
                        pt={2}
                        pl={[4, 4, 4, 6]}
                        transition="margin 150ms ease-in-out"
                        sx={{ containerType: "inline-size" }}
                    >
                        <Box as={"h2"} display="flex" flexDirection="column">
                            <Text
                                as={"span"}
                                fontWeight="bold"
                                className="product-name"
                                fontSize={["desktopBodyTextSmall", "desktopBodyTextNormal"]}
                                textTransform="capitalize"
                                color="black"
                                {...themeText}
                            >
                                {productNameTransform(productName)}
                            </Text>
                            {!isExpanded && descriptiveHeading && (
                                <Text
                                    as={"span"}
                                    fontWeight="bold"
                                    color="gray.500"
                                    className="product-colors"
                                    fontSize={["desktopBodyTextSmall", "desktopBodyTextNormal"]}
                                    {...themeTextGray}
                                >
                                    {descriptiveHeading}
                                </Text>
                            )}
                        </Box>
                        {/* Colors */}
                        <Text
                            fontWeight="bold"
                            color="gray.500"
                            className="product-colors"
                            fontSize={["desktopBodyTextSmall", "desktopBodyTextNormal"]}
                            {...themeTextGray}
                        >
                            {!isExpanded ? colorLabel : null}
                        </Text>
                        {/* Product Price, Previous Lowest Price, and their respective discount percentages */}
                        {isOmnibusMarket ? (
                            <>
                                <Money
                                    data={displayedProduct.priceWithDiscount}
                                    locale={locale}
                                    fontWeight="bold"
                                    color={
                                        isDiscounted && displayDiscountPercentage > 0
                                            ? "red.100"
                                            : theme
                                              ? themeText?.color
                                              : "black"
                                    }
                                    mt={isExpanded ? 0 : 3}
                                    className="product-price"
                                    fontSize="desktopBodyTextNormal"
                                    {...(theme && !isDiscounted ? { ...themeText } : {})}
                                />
                                <Flex
                                    fontSize="desktopBodyTextTiny"
                                    mb={2}
                                    flexDir="column"
                                    gap={1}
                                >
                                    {showPreviousLowestPrice && (
                                        <DiscountedPriceBlock
                                            label={microcopies[CommonCMS.price.previousLowestPrice]}
                                            price={displayPreviousPrice as SimpleMoneyData}
                                            dataTestId="PreviousLowestPrice"
                                            locale={locale}
                                            isOmnibusMarket={true}
                                            discount={displayDiscountPercentage}
                                            {...themeTextGray}
                                        />
                                    )}
                                    {showRecommendedPrice && (
                                        <DiscountedPriceBlock
                                            label={microcopies[CommonCMS.price.recommendedPrice]}
                                            price={
                                                displayedProduct.priceWithoutDiscount as SimpleMoneyData
                                            }
                                            dataTestId="RecommendedPrice"
                                            locale={locale}
                                        />
                                    )}
                                </Flex>
                            </>
                        ) : (
                            <Flex
                                flexDir="row"
                                gap={2}
                                mt={isExpanded ? 0 : 3}
                                fontSize={["desktopBodyTextSmall", "desktopBodyTextNormal"]}
                                flexWrap={["wrap", "nowrap"]}
                                sx={{
                                    transition: "margin 150ms ease-in-out",
                                }}
                            >
                                <Money
                                    data={displayedProduct.priceWithDiscount}
                                    locale={locale}
                                    fontWeight="bold"
                                    color="black"
                                    className="product-price"
                                    ariaLabel={microcopies[CommonCMS.price.discountedPrice]}
                                    {...themeText}
                                />
                                {Boolean(displayedProduct.discountPercentage) && (
                                    <Money
                                        data={displayedProduct.priceWithoutDiscount}
                                        locale={locale}
                                        fontWeight="bold"
                                        color="gray.500"
                                        textDecoration="line-through"
                                        className="product-discounted-price"
                                        ariaLabel={microcopies[CommonCMS.price.recommendedPrice]}
                                        {...themeTextGray}
                                    />
                                )}
                            </Flex>
                        )}
                    </Box>
                </Box>
            </NextLink>
            {/* As soon the link wrapper get focus, we need this element to be here to make it focusable after jumping from link. */}
            {(isExpanded || isMobile) && (
                <Box onMouseEnter={onOpen} onMouseLeave={onClose}>
                    <Flex position="absolute" top={1} right={1} zIndex="1">
                        <Flex gap={[0, 4, 6]} justifyContent="flex-end">
                            <IconButton
                                tabIndex={0}
                                size={"sm"}
                                aria-label="Add to wishlist"
                                color={"black"}
                                variant="tertiary"
                                sx={{
                                    bgColor: "transparent",
                                    "&:hover": {
                                        bgColor: "transparent",
                                    },
                                }}
                                icon={
                                    <ECCOIcon
                                        name={isAddingToWishlist ? "wishlist-filled" : "wishlist"}
                                    />
                                }
                                onClick={addToWishlist}
                            />
                        </Flex>
                    </Flex>
                </Box>
            )}
        </Box>
    );
};

const DiscountedPriceBlock = ({
    label,
    price,
    dataTestId,
    locale,
    isOmnibusMarket,
    discount,
}: {
    label: string;
    price: SimpleMoneyData;
    dataTestId: string;
    locale: string;
    isOmnibusMarket?: boolean;
    discount?: number;
}) => (
    <Flex color="gray.500" flexWrap="wrap" gap={1} alignItems="center">
        <Text fontSize="inherit" fontWeight="bold" color="gray.500">
            {label}
        </Text>
        <Money
            textDecoration={discount > 0 ? "line-through" : "none"}
            locale={locale}
            data-testid={dataTestId}
            data={price}
        />
        {discount > 0 && isOmnibusMarket && (
            <Text fontSize="inherit" fontWeight="bold" color="red.100">{`-${discount}%`}</Text>
        )}
    </Flex>
);
